"use client"

import { Form, Formik } from "formik"
import { AlertTriangle, Loader2, Save, Trash2 } from "lucide-react"

import { CrmReadyToShipStageField } from "@/components/deliveries/crm-ready-to-ship-stage-field"
import { FormInput, FormSelect } from "@/components/form-inputs"
import { Alert, AlertDescription } from "@/components/ui/alert"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import {
  useClearTenantDeliveryDefaultsMutation,
  useSaveTenantDeliveryDefaultsMutation,
} from "@/hooks/mutations/delivery-bindings"
import { useTenantDeliveryDefaultsQuery } from "@/hooks/queries/delivery-bindings"
import type { TenantDeliveryDefaultsView } from "@/server-actions/delivery-bindings"
import type { DeliveryIntegrationView } from "@/server-actions/delivery-integrations"
import { useTranslations } from "next-intl"



type TenantDefaultsFormValues = {
  integrationId: string
  merchandisePaymentPolicy:
    | "prepayment_only"
    | "cash_on_delivery_only"
    | "customer_choice"
  deliveryPriceResponsibility: "business" | "customer"
  crmStageId: string
  crmTags: string
}

export function DeliveryTenantDefaultsForm({
  integrations,
}: {
  integrations: DeliveryIntegrationView[]
}) {

  const tUi = useTranslations("Deliveries.DeliveriesDeliveryTenantDefaultsForm")

  const defaultsQuery = useTenantDeliveryDefaultsQuery()
  const saveMutation = useSaveTenantDeliveryDefaultsMutation()
  const clearMutation = useClearTenantDeliveryDefaultsMutation()
  const activeIntegrations = integrations.filter(integration => (
    integration.isActive && integration.verificationStatus === "succeeded"
  ))
  const initialValues = createInitialValues(
    defaultsQuery.data,
    activeIntegrations,
  )
  const busy = saveMutation.isPending || clearMutation.isPending

  if (defaultsQuery.isLoading) {
    return <div className="h-44 animate-pulse rounded-md bg-muted" />
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base"><span key={tUi("defaultForAllAgents")}>{tUi("defaultForAllAgents")}</span></CardTitle>
        <CardDescription>
          <span key={tUi("setsTheFallbackDeliveryIntegrationAndPaymentPolicy")}>{tUi("setsTheFallbackDeliveryIntegrationAndPaymentPolicy")}</span></CardDescription>
      </CardHeader>
      <CardContent>
        {defaultsQuery.error ? (
          <Alert variant="destructive" className="mb-4">
            <AlertDescription><span key={tUi("deliveryDefaultsCouldNotBeLoaded")}>{tUi("deliveryDefaultsCouldNotBeLoaded")}</span></AlertDescription>
          </Alert>
        ) : null}
        {defaultsQuery.data?.consistent === false ? (
          <Alert className="mb-4">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>
              <span key={tUi("existingChannelDefaultsDifferSavingHereReplacesAll")}>{tUi("existingChannelDefaultsDifferSavingHereReplacesAll")}</span></AlertDescription>
          </Alert>
        ) : null}

        <Formik<TenantDefaultsFormValues>
          enableReinitialize
          initialValues={initialValues}
          validate={validateTenantDefaults}
          onSubmit={(values, helpers) => {
            saveMutation.mutate({
              integrationId: values.integrationId,
              merchandisePaymentPolicy: values.merchandisePaymentPolicy,
              deliveryPriceResponsibility: values.deliveryPriceResponsibility,
              crmSignal: normalizeCrmSignal(values.crmStageId, values.crmTags),
              crmFieldMapping: defaultsQuery.data?.selection?.crmFieldMapping,
              setErrors: helpers.setErrors,
            }, {
              onSettled: () => helpers.setSubmitting(false),
            })
          }}
        >
          {({ isSubmitting, values }) => {
            const selected = activeIntegrations.find(
              integration => integration.id === values.integrationId,
            )
            const cashOnDeliveryAvailable = selected
              ? supportsCashOnDelivery(selected)
              : false

            return (
              <Form className="space-y-4">
                <FormSelect
                  name="integrationId"
                  label={tUi("defaultDeliveryIntegration")}
                  placeholder={tUi("selectAnActiveIntegration")}
                  options={activeIntegrations.map(integration => ({
                    value: integration.id,
                    label: integration.name,
                  }))}
                />
                <FormSelect
                  name="merchandisePaymentPolicy"
                  label={tUi("customerMerchandisePaymentOptions")}
                  options={getPaymentPolicyOptions(cashOnDeliveryAvailable, tUi)}
                  description={paymentPolicyDescription(
                    values.merchandisePaymentPolicy,
                    cashOnDeliveryAvailable, tUi
                  )}
                />
                {!cashOnDeliveryAvailable ? (
                  <p className="text-xs text-muted-foreground">
                    <span key={tUi("cashOnDeliveryRemainsUnavailableUntilNovaPost")}>{tUi("cashOnDeliveryRemainsUnavailableUntilNovaPost")}</span></p>
                ) : null}
                <FormSelect
                  name="deliveryPriceResponsibility"
                  label={tUi("whoPaysTheCarrierDeliveryPrice")}
                  options={[
                    { value: "business", label: tUi("businessMerchant") },
                    { value: "customer", label: tUi("customer") },
                  ]}
                />
                <CrmReadyToShipStageField />
                <FormInput
                  name="crmTags"
                  label={tUi("cRMReadyToShipTagsOptional")}
                  maxLength={500}
                  description={tUi("commaSeparatedTagsTheWorkerNoteIsAlways")}
                />
                <div className="flex flex-wrap gap-2">
                  <Button
                    type="submit"
                    disabled={busy || isSubmitting || activeIntegrations.length === 0}
                  >
                    {saveMutation.isPending ? (
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    ) : (
                      <Save className="mr-2 h-4 w-4" />
                    )}
                    <span><span key={tUi("saveDefaults")}>{tUi("saveDefaults")}</span></span>
                  </Button>
                  {defaultsQuery.data?.selection ? (
                    <Button
                      type="button"
                      variant="outline"
                      disabled={busy}
                      onClick={() => clearMutation.mutate()}
                    >
                      <Trash2 className="mr-2 h-4 w-4" />
                      <span><span key={tUi("clearDefaults")}>{tUi("clearDefaults")}</span></span>
                    </Button>
                  ) : null}
                </div>
              </Form>
            )
          }}
        </Formik>
      </CardContent>
    </Card>
  )
}

function createInitialValues(
  defaults: TenantDeliveryDefaultsView | undefined,
  activeIntegrations: DeliveryIntegrationView[],
): TenantDefaultsFormValues {
  return {
    integrationId: findInitialIntegrationId(defaults, activeIntegrations),
    merchandisePaymentPolicy:
      defaults?.selection?.merchandisePaymentPolicy ?? "customer_choice",
    deliveryPriceResponsibility:
      defaults?.selection?.deliveryPriceResponsibility ?? "business",
    crmStageId: defaults?.selection?.crmSignal?.stageId ?? "",
    crmTags: defaults?.selection?.crmSignal?.tags?.join(", ") ?? "",
  }
}

function findInitialIntegrationId(
  defaults: TenantDeliveryDefaultsView | undefined,
  activeIntegrations: DeliveryIntegrationView[],
): string {
  const configuredId = defaults?.selection?.integrationId
  if (
    configuredId
    && activeIntegrations.some(integration => integration.id === configuredId)
  ) {
    return configuredId
  }
  return activeIntegrations[0]?.id ?? ""
}

function validateTenantDefaults(
  values: TenantDefaultsFormValues,
): Partial<Record<keyof TenantDefaultsFormValues, string>> {
  const errors: Partial<Record<keyof TenantDefaultsFormValues, string>> = {}
  if (!values.integrationId) {
    errors.integrationId = "Select an active delivery integration."
  }
  return errors
}

function normalizeCrmSignal(
  stageIdInput: string,
  tagsInput: string,
): { stageId?: string; tags?: string[] } | undefined {
  const stageId = stageIdInput.trim() || undefined
  const tags = tagsInput.split(",")
    .map(tag => tag.trim())
    .filter(Boolean)
  if (!stageId && tags.length === 0) return undefined
  return { stageId, tags: tags.length > 0 ? tags : undefined }
}

function supportsCashOnDelivery(
  integration: DeliveryIntegrationView,
): boolean {
  return integration.supportsCashOnDelivery
}

function getPaymentPolicyOptions(cashOnDeliveryAvailable: boolean, tUi: ReturnType<typeof useTranslations<"Deliveries.DeliveriesDeliveryTenantDefaultsForm">>) {
  const options = [
    {
      value: "customer_choice",
      label: cashOnDeliveryAvailable
        ? "Let the customer choose"
        : "Use available methods (prepayment only)",
    },
    { value: "prepayment_only", label: tUi("prepaymentOnly") },
  ] as Array<{ value: TenantDefaultsFormValues["merchandisePaymentPolicy"]; label: string }>
  if (cashOnDeliveryAvailable) {
    options.push({
      value: "cash_on_delivery_only",
      label: tUi("cashOnDeliveryOnly"),
    })
  }
  return options
}

function paymentPolicyDescription(
  policy: TenantDefaultsFormValues["merchandisePaymentPolicy"],
  cashOnDeliveryAvailable: boolean,tUi: ReturnType<typeof useTranslations<"Deliveries.DeliveriesDeliveryTenantDefaultsForm">>
): string {
  switch (policy) {
    case "prepayment_only":
      return tUi("theCustomerPaysTheMerchantBeforeDeliveryThe")
    case "cash_on_delivery_only":
      return tUi("novaPostCollectsTheMerchandisePaymentAndSettles")
    case "customer_choice":
      return cashOnDeliveryAvailable
        ? "The bot offers prepayment and Nova Post payment on delivery, asks the customer to choose, and records the selection in CRM."
        : "Nova Post payment on delivery is not enabled for this connected sender account, so the bot uses prepayment and must not ask the customer to choose."
    default: {
      const exhaustivePolicy: never = policy
      return exhaustivePolicy
    }
  }
}
